import { useState, useCallback, useEffect, useRef } from 'react';

const parseUrlState = () => {
  const params = new URLSearchParams(window.location.search);
  const filters = {};
  let sortBy = null;
  let sortOrder = 'asc';

  params.forEach((value, key) => {
    if (!key.startsWith('q[')) return;
    if (key === 'q[s]') {
      const [field, dir] = value.split(' ');
      if (field) {
        sortBy = field;
        sortOrder = dir === 'desc' ? 'desc' : 'asc';
      }
    } else if (value !== '') {
      filters[key] = value;
    }
  });

  const pageParam = parseInt(params.get('page'), 10);
  const page = Number.isInteger(pageParam) && pageParam > 0 ? pageParam : 1;

  return { filters, page, sortBy, sortOrder };
};

/**
 * Manages server-side paginated, sortable, and filterable data fetching
 * for admin resource index pages.
 *
 * @param {string} endpoint  Base Rails path, e.g. '/admin_kit/dealers'
 * @param {number} perPage   Page size (default 30)
 */
const EMPTY_STATE = { filters: {}, page: 1, sortBy: null, sortOrder: 'asc' };

const useAdminResource = (endpoint, perPage = 30) => {
  const managesUrl = endpoint === window.location.pathname;
  const initial = useRef(managesUrl ? parseUrlState() : EMPTY_STATE).current;

  const [records, setRecords] = useState([]);
  const [isLoading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFiltersState] = useState(initial.filters);
  const [page, setPage] = useState(initial.page);
  const [sortBy, setSortBy] = useState(initial.sortBy);
  const [sortOrder, setSortOrder] = useState(initial.sortOrder);
  const [pagination, setPagination] = useState({
    page: 1,
    per_page: perPage,
    total_pages: 0,
    total_items: 0,
  });

  // Keep a ref to the latest fetch so stale responses are ignored
  const fetchId = useRef(0);

  const fetch = useCallback(async () => {
    fetchId.current += 1;
    const id = fetchId.current;
    setLoading(true);
    setError(null);

    const params = new URLSearchParams({ page, per_page: perPage });
    Object.entries(filters).forEach(([k, v]) => {
      if (v !== '' && v != null) params.set(k, v);
    });
    if (sortBy) {
      params.set('q[s]', `${sortBy} ${sortOrder}`);
    }

    try {
      const res = await window.fetch(`${endpoint}.json?${params}`);
      if (!res.ok) throw new Error(`Server error: ${res.status}`);
      const data = await res.json();
      if (fetchId.current !== id) return; // stale
      setRecords(data.records);
      setPagination(data.pagination);
    } catch (err) {
      if (fetchId.current === id) setError(err.message);
    } finally {
      if (fetchId.current === id) setLoading(false);
    }
  }, [endpoint, page, perPage, filters, sortBy, sortOrder]);

  useEffect(() => {
    fetch();
  }, [fetch]);

  useEffect(() => {
    if (!managesUrl) return;

    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => {
      if (v !== '' && v != null) params.set(k, v);
    });
    if (sortBy) params.set('q[s]', `${sortBy} ${sortOrder}`);
    if (page > 1) params.set('page', page);

    const qs = params.toString();
    const url = `${window.location.pathname}${qs ? `?${qs}` : ''}`;
    window.history.replaceState(window.history.state, '', url);
  }, [managesUrl, filters, page, sortBy, sortOrder]);

  // Reset to page 1 whenever filters change
  const setFilters = useCallback((newFilters) => {
    setPage(1);
    setFiltersState(newFilters);
  }, []);

  const handleSort = (field) => () => {
    if (sortBy === field) {
      setSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
    setPage(1);
  };

  return {
    records,
    isLoading,
    error,
    pagination,
    page,
    setPage,
    filters,
    setFilters,
    sortBy,
    sortOrder,
    handleSort,
    refetch: fetch,
  };
};

export default useAdminResource;
